import React, { useMemo } from "react";
import "chart.js/auto";
import { Line } from "react-chartjs-2";
import moment from "moment";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

export default function TransferVolumeChart({ samples = [], baseline, capacity, direction, height = 200 }) {
  const theme = useTheme();
  const { palette, functions: { rgba } } = theme;
  const lineColor = direction === "drop" ? palette.primary.main : palette.info.main;

  const data = useMemo(() => ({
    labels: samples.map((sample) => moment(sample.t).format("h:mm:ss A")),
    datasets: [
      {
        label: "Gallons",
        data: samples.map((sample) => Math.round(sample.g)),
        borderColor: lineColor,
        backgroundColor: rgba(lineColor, 0.15),
        fill: true,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2.5,
      },
      ...(baseline != null
        ? [{
            label: "Start",
            data: samples.map(() => Math.round(baseline)),
            borderColor: rgba(palette.text.main, 0.5),
            borderDash: [4, 4],
            borderWidth: 1,
            pointRadius: 0,
            fill: false,
          }]
        : []),
    ],
  }), [samples, baseline, lineColor, palette.text.main, rgba]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()} gal` } },
    },
    scales: {
      x: { ticks: { color: palette.text.main, maxTicksLimit: 6 }, grid: { display: false } },
      y: {
        min: 0,
        max: capacity || undefined,
        ticks: { color: palette.text.main, callback: (value) => Number(value).toLocaleString() },
        grid: { color: rgba(palette.text.main, 0.08) },
      },
    },
  }), [capacity, palette.text.main, rgba]);

  if (samples.length < 2) {
    return (
      <MDBox height={height} display="flex" alignItems="center" justifyContent="center">
        <MDTypography variant="button" color="text">
          Waiting for transfer samples…
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox height={height}>
      <Line data={data} options={options} />
    </MDBox>
  );
}
